// @ts-nocheck
import React from 'react';
import { useTheme } from '../contexts/ThemeContext';
import { Brain, TrendingUp, Shield, Zap, Target, Users, Award, Rocket } from 'lucide-react';
import PublicHeader from '../components/PublicHeader';
import Footer from '../components/Footer';

const About: React.FC = () => {
  const { isDark } = useTheme();

  const values = [
    {
      icon: Brain,
      title: 'AI-Powered Intelligence',
      description: 'Leveraging cutting-edge AI to analyze crypto markets and provide actionable insights.',
      color: 'purple'
    },
    {
      icon: TrendingUp,
      title: 'Data-Driven Decisions',
      description: 'Real-time market data, technical indicators, and sentiment analysis at your fingertips.',
      color: 'blue'
    },
    {
      icon: Shield,
      title: 'Secure & Reliable',
      description: 'Enterprise-grade security and reliable infrastructure to keep your data safe.',
      color: 'emerald'
    },
    {
      icon: Zap,
      title: 'Lightning Fast',
      description: 'Instant analysis and real-time updates to help you stay ahead of the market.',
      color: 'orange'
    }
  ];

  const mission = [
    {
      icon: Target,
      title: 'Our Mission',
      description: 'To democratize cryptocurrency trading by providing professional-grade AI analysis tools accessible to everyone, from beginners to experienced traders.'
    },
    {
      icon: Users,
      title: 'Our Community',
      description: 'Building a community of informed traders who make data-driven decisions using advanced AI technology and comprehensive market analysis.'
    },
    {
      icon: Rocket,
      title: 'Our Vision',
      description: 'To become the leading AI-powered cryptocurrency analysis platform, empowering traders worldwide with intelligent insights and strategic guidance.'
    }
  ];

  const getColorClasses = (color: string) => {
    const colors: Record<string, { bg: string; text: string; icon: string }> = {
      purple: {
        bg: isDark ? 'bg-purple-500/10' : 'bg-purple-50',
        text: isDark ? 'text-purple-400' : 'text-purple-600',
        icon: isDark ? 'text-purple-400' : 'text-purple-600'
      },
      blue: {
        bg: isDark ? 'bg-blue-500/10' : 'bg-blue-50',
        text: isDark ? 'text-blue-400' : 'text-blue-600',
        icon: isDark ? 'text-blue-400' : 'text-blue-600'
      },
      emerald: {
        bg: isDark ? 'bg-emerald-500/10' : 'bg-emerald-50',
        text: isDark ? 'text-emerald-400' : 'text-emerald-600',
        icon: isDark ? 'text-emerald-400' : 'text-emerald-600'
      },
      orange: {
        bg: isDark ? 'bg-orange-500/10' : 'bg-orange-50',
        text: isDark ? 'text-orange-400' : 'text-orange-600',
        icon: isDark ? 'text-orange-400' : 'text-orange-600'
      }
    };
    return colors[color] || colors.blue;
  };

  return (
    <div className={`min-h-screen ${isDark ? 'bg-zinc-950' : 'bg-gray-50'}`}>
      {/* Header */}
      <PublicHeader />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Hero Section */}
        <div className="text-center mb-16">
          <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full mb-6 ${
            isDark
              ? 'bg-gradient-to-r from-blue-500/20 to-purple-500/20 border border-blue-500/30'
              : 'bg-gradient-to-r from-blue-50 to-purple-50 border border-blue-200'
          }`}>
            <Award className={`w-4 h-4 ${isDark ? 'text-blue-400' : 'text-blue-600'}`} />
            <span className={`text-sm font-semibold ${isDark ? 'text-blue-400' : 'text-blue-600'}`}>
              About InvestVerse.AI
            </span>
          </div>

          <h1 className={`text-4xl md:text-5xl font-bold mb-6 ${
            isDark ? 'text-white' : 'text-gray-900'
          }`}>
            AI-Powered Crypto Trading Intelligence
          </h1>

          <p className={`text-lg md:text-xl max-w-3xl mx-auto ${
            isDark ? 'text-zinc-400' : 'text-gray-600'
          }`}>
            InvestVerse.AI combines advanced artificial intelligence with real-time market data to provide
            comprehensive cryptocurrency analysis, helping traders make informed decisions in the dynamic crypto market.
          </p>
        </div>

        {/* What We Do Section */}
        <div className="mb-20">
          <h2 className={`text-3xl font-bold text-center mb-12 ${
            isDark ? 'text-white' : 'text-gray-900'
          }`}>
            What We Do
          </h2>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((value, index) => {
              const colors = getColorClasses(value.color);
              const Icon = value.icon;

              return (
                <div
                  key={index}
                  className={`p-6 rounded-2xl border transition-all duration-200 hover:scale-105 ${
                    isDark
                      ? 'bg-white/5 border-white/10 hover:bg-white/10'
                      : 'bg-white border-gray-200 hover:shadow-lg'
                  }`}
                >
                  <div className={`inline-flex items-center justify-center w-12 h-12 rounded-xl mb-4 ${colors.bg}`}>
                    <Icon className={`w-6 h-6 ${colors.icon}`} />
                  </div>

                  <h3 className={`text-lg font-semibold mb-2 ${
                    isDark ? 'text-white' : 'text-gray-900'
                  }`}>
                    {value.title}
                  </h3>

                  <p className={`text-sm ${
                    isDark ? 'text-zinc-400' : 'text-gray-600'
                  }`}>
                    {value.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>

        {/* Mission, Community, Vision Section */}
        <div className="mb-20">
          <div className="grid md:grid-cols-3 gap-8">
            {mission.map((item, index) => {
              const Icon = item.icon;

              return (
                <div
                  key={index}
                  className={`p-8 rounded-2xl border ${
                    isDark
                      ? 'bg-white/5 border-white/10'
                      : 'bg-white border-gray-200'
                  }`}
                >
                  <div className={`inline-flex items-center justify-center w-14 h-14 rounded-xl mb-6 ${
                    isDark ? 'bg-blue-500/10' : 'bg-blue-50'
                  }`}>
                    <Icon className={`w-7 h-7 ${isDark ? 'text-blue-400' : 'text-blue-600'}`} />
                  </div>

                  <h3 className={`text-xl font-bold mb-3 ${
                    isDark ? 'text-white' : 'text-gray-900'
                  }`}>
                    {item.title}
                  </h3>

                  <p className={`text-base leading-relaxed ${
                    isDark ? 'text-zinc-400' : 'text-gray-600'
                  }`}>
                    {item.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>

        {/* Features Highlight */}
        <div className={`rounded-2xl border p-8 md:p-12 ${
          isDark
            ? 'bg-gradient-to-br from-blue-500/10 to-purple-500/10 border-blue-500/20'
            : 'bg-gradient-to-br from-blue-50 to-purple-50 border-blue-200'
        }`}>
          <h2 className={`text-3xl font-bold text-center mb-6 ${
            isDark ? 'text-white' : 'text-gray-900'
          }`}>
            Why Choose InvestVerse.AI?
          </h2>

          <p className={`text-center text-lg mb-8 max-w-3xl mx-auto ${
            isDark ? 'text-zinc-300' : 'text-gray-700'
          }`}>
            We leverage GPT-4 powered AI agents to analyze multiple data sources including market trends,
            technical indicators, on-chain metrics, sentiment analysis, and fundamentals to provide you with
            comprehensive trading insights.
          </p>

          <div className="grid md:grid-cols-2 gap-6">
            <div className={`p-6 rounded-xl ${
              isDark ? 'bg-white/5 border border-white/10' : 'bg-white border border-gray-200'
            }`}>
              <h4 className={`font-semibold mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                Multi-Agent Analysis
              </h4>
              <p className={`text-sm ${isDark ? 'text-zinc-400' : 'text-gray-600'}`}>
                Specialized AI agents for market analysis, technical analysis, sentiment, fundamentals, and risk assessment.
              </p>
            </div>

            <div className={`p-6 rounded-xl ${
              isDark ? 'bg-white/5 border border-white/10' : 'bg-white border border-gray-200'
            }`}>
              <h4 className={`font-semibold mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                Real-Time Data
              </h4>
              <p className={`text-sm ${isDark ? 'text-zinc-400' : 'text-gray-600'}`}>
                Live market data, price feeds, whale tracking, and on-chain metrics updated in real-time.
              </p>
            </div>

            <div className={`p-6 rounded-xl ${
              isDark ? 'bg-white/5 border border-white/10' : 'bg-white border border-gray-200'
            }`}>
              <h4 className={`font-semibold mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                Advanced Indicators
              </h4>
              <p className={`text-sm ${isDark ? 'text-zinc-400' : 'text-gray-600'}`}>
                Technical indicators including MACD, RSI, SMA, Bollinger Bands, and more with 365+ days of historical data.
              </p>
            </div>

            <div className={`p-6 rounded-xl ${
              isDark ? 'bg-white/5 border border-white/10' : 'bg-white border border-gray-200'
            }`}>
              <h4 className={`font-semibold mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                Actionable Insights
              </h4>
              <p className={`text-sm ${isDark ? 'text-zinc-400' : 'text-gray-600'}`}>
                Clear trading recommendations, risk levels, and strategic guidance backed by comprehensive analysis.
              </p>
            </div>
          </div>
        </div>

        {/* CTA Section */}
        <div className="text-center mt-16">
          <p className={`text-lg mb-4 ${isDark ? 'text-zinc-400' : 'text-gray-600'}`}>
            Ready to elevate your crypto trading?
          </p>
          <a
            href="/agent-analysis"
            className="inline-flex items-center gap-2 px-8 py-4 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-xl transition-all duration-200 transform hover:scale-105"
          >
            <Brain className="w-5 h-5" />
            Start Using AI Agents
          </a>
        </div>
      </div>

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default About;
